import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, MessageSquare, Search, FileText, Shield } from 'lucide-react';
import { processSteps } from '../data/mock';
import { Button } from './ui/button';
import { Card } from './ui/card';

const iconMap = {
  MessageSquare,
  Search,
  FileText,
  Shield
};

const ProcessSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % processSteps.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % processSteps.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + processSteps.length) % processSteps.length);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Our Simple Process
          </h2>
          <p className="text-lg text-gray-600">
            From consultation to protection in four easy steps
          </p>
        </div>

        {/* Desktop View */}
        <div className="hidden md:grid md:grid-cols-4 gap-6">
          {processSteps.map((step, index) => {
            const Icon = iconMap[step.icon];
            return (
              <Card key={step.id} className="p-6 text-center hover:shadow-lg transition-shadow duration-300 border-2 border-gray-200 hover:border-teal-400">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon className="h-8 w-8 text-teal-600" />
                </div>
                <div className="text-teal-600 font-bold text-sm mb-2">Step {step.id}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.description}</p>
              </Card>
            );
          })}
        </div>

        {/* Mobile/Tablet Slider View */}
        <div className="md:hidden">
          <div className="relative">
            <Card className="p-8 bg-white shadow-xl rounded-2xl border-2 border-teal-400">
              {(() => {
                const step = processSteps[currentIndex];
                const Icon = iconMap[step.icon];
                return (
                  <div className="text-center">
                    <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Icon className="h-10 w-10 text-teal-600" />
                    </div>
                    <div className="text-teal-600 font-bold text-sm mb-2">Step {step.id}</div>
                    <h3 className="text-xl font-bold text-slate-900 mb-4">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                );
              })()}
            </Card>

            <div className="flex justify-center gap-4 mt-8">
              <Button
                onClick={prevSlide}
                variant="outline"
                size="icon"
                className="rounded-full border-2 border-teal-600 text-teal-600 hover:bg-teal-50"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2">
                {processSteps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`h-3 w-3 rounded-full transition-all duration-300 ${
                      index === currentIndex
                        ? 'bg-teal-600 w-8'
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
              <Button
                onClick={nextSlide}
                variant="outline"
                size="icon"
                className="rounded-full border-2 border-teal-600 text-teal-600 hover:bg-teal-50"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSlider;